import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {RouteComponentProps, useLocation, useParams} from "react-router";
import {css, Theme} from "@emotion/react";

import {WideContent} from "@pg-design/grid-module";
import {calculateRemSize, flex, mb, pl, pt} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {FaqSection} from "@pg-mono/faq";
import {useScrollObserver} from "@pg-mono/hooks";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IOfferFriendlySubFilterGeneralUrlParams} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpFeatureFlagNewUserFlow} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {LocationLinks} from "../../atoms/LocationLinks";
import {useOneTap} from "../../auth/hooks/use_one_tap";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {useGetOfferListModularHouses} from "../../modular_houses/hooks/use_get_modular_house_offer_list";
import {FavouritesInfoModal} from "../../my_offers/components/FavouritesInfoModal/FavouritesInfoModal";
import {ListingBanner} from "../../property_configurator/components/ListingBanner";
import {Country} from "../../region/types/Country";
import {useSearchGuideTour} from "../../search/hooks/use_search_guide_tour";
import {UseGeolocationWithAlgolytics} from "../../search/utils/use_geolocation_with_algolytics";
import {useRegionAdServerRefresh} from "../../smart_ad_server/hooks/use_region_ad_server_refresh";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {OfferListLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {OfferChatBot} from "../components/offer_chatbot/OfferChatBot";
import {OfferListViewNavigation} from "../components/OfferListViewNavigation";
import {offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {useSmartAdServerOnOfferList} from "../hooks/use_smart_ad_server_on_offer_list";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {OfferAveragePrice} from "../list/components/OfferAveragePrice/OfferAveragePrice";
import {OfferListGenericLocationDescription} from "../list/components/OfferListGenericLocationDescription/OfferListGenericLocationDescription";
import {OfferListListSection} from "../list/components/OfferListListSection/OfferListListSection";
import {OfferListNearbyOffers} from "../list/components/OfferListNearbyOffers";
import {OfferListSeoDescription} from "../list/components/OfferListSeoDescription";
import {OFFER_LIST_PAGE_SIZE} from "../list/constants/offer_list";
import {offerListGuideTourName, offerListGuideTourSteps} from "../list/constants/offer_list_guide_tour";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_query";
import {useFetchOfferList} from "../list/hooks/use_fetch_offer_list";
import {usePageLinkBuilder} from "../list/hooks/usePageLinkBuilder";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {SchemaListItem} from "../schemas/SchemaListItem";
import {OfferType} from "../types/OfferType";
import {getChatBotStorage, setChatBotStorage} from "../utils/chat_bot_storage";
import {getListingPageNearbyOffersPayload} from "../utils/get_listing_page_nearby_offers_payload";

interface IOwnProps {}

export interface IProps extends IOwnProps, RouteComponentProps<{offerListSubType?: OfferListSubType}> {}

export const OfferListView = (props: IProps) => {
    //  Init
    const {isMobile} = useUserDevice();
    const fetchOfferList = useFetchOfferList();
    const offerListRef = useRef<HTMLDivElement | null>(null);

    const chatBotStorage = getChatBotStorage();
    const [shouldOfferListConverstionBeOpened, setShouldOfferListConversationBeOpened] = useState(false);
    const shouldChatBotConversationBeOpened = chatBotStorage
        ? !chatBotStorage.didShowAutomatically && shouldOfferListConverstionBeOpened
        : shouldOfferListConverstionBeOpened;

    /*
       ClickBot related logic commented due to task - CU-8695f79b0

        const clickBotStorage = getClickBotStorage();
        const shouldClickBotConversationBeOpened = clickBotStorage
            ? !clickBotStorage.didShowAutomatically && shouldOfferListConverstionBeOpened
            : shouldOfferListConverstionBeOpened;
     */

    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);
    const list = useSelector((state: IRPStore) => state.offerList.list);
    const articles = useSelector((state: IRPStore) => state.offerList.articles);
    const [initialFetchDone, setInitialFetchDone] = useState(false);

    const search = useSelector((state: IRPStore) => state.offerList.search);
    const nearbyRegions = useSelector((state: IRPStore) => state.offerList.nearbyRegions);
    const recommendedRegions = useSelector((state: IRPStore) => state.offerList.recommendedRegions);
    const metaDataTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const faq = useSelector((state: IRPStore) => state.faq.data);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const location = useLocation();
    const latestQuery = useSelector((state: IRPStore) => state.offerList.list.latestQuery);
    const countryId = getCountryId(latestQuery.country);
    const modularHouseCache = useGetOfferListModularHouses();
    const isListEmpty = list.offers.length === 0;
    const searchLocationTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {locationAsked, locateUser} = UseGeolocationWithAlgolytics();
    const {startGuideTour, hasGuideTourBeenCompleted, guideTourCurrentStepId, tourStorageState} = useSearchGuideTour({name: offerListGuideTourName});

    useEffect(() => {
        /**
         * fetch offers on init including default distance on browser side - CU-862jpkfcy
         *
         * after dependencies changes in useEffect that triggers hitSearch search event was triggered 2 times on first list view entry, function in .then prevents this
         */
        fetchOfferList().then(() => {
            setInitialFetchDone(true);
        });
    }, []);

    useEffect(() => {
        if (!rpFeatureFlagNewUserFlow) {
            return;
        }

        // start guide tour on mount only if stage has not been set and guide tour has not been started
        if (tourStorageState && !tourStorageState?.hasBeenSeen && typeof guideTourCurrentStepId !== "number") {
            startGuideTour(offerListGuideTourSteps);
        }
    }, [tourStorageState]);

    useOneTap({
        disable: rpFeatureFlagNewUserFlow ? !hasGuideTourBeenCompleted : false
    });

    useScrollObserver(
        ({scrollY}) => {
            const offerListOffsetTop = offerListRef.current?.offsetTop || 0;
            const offerListHeight = offerListRef.current?.clientHeight || 0;
            const breakpoint = offerListHeight / 3 + offerListOffsetTop;
            const locationAskBreakpoint = (offerListHeight + offerListOffsetTop) / 10;

            if (!locationAsked && scrollY >= locationAskBreakpoint) {
                locateUser();
            }

            if (breakpoint && scrollY >= breakpoint) {
                setShouldOfferListConversationBeOpened(true);
            }
        },
        [offerListRef.current, locationAsked],
        !shouldChatBotConversationBeOpened
    );

    const singleRegion = selectedRegions.length === 1 ? selectedRegions[0] : undefined;
    const query = parseSearch(location.search);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const buildPageLink = usePageLinkBuilder();
    const {offerListSubFilter} = useParams<Partial<IOfferFriendlySubFilterGeneralUrlParams>>();

    const offerParams = useMemo(() => {
        const friendlySlug = (props.match.params as {friendlySlug?: string}).friendlySlug;
        return friendlySlug ? offerUrlParser(friendlySlug) : null;
    }, [props.match.params]);
    const {data: nearbyOffersResponse} = useGetOfferListQuery(getListingPageNearbyOffersPayload({offerParams, singleRegion}), {
        skip: list.requestState !== RequestState.Success
    });
    const offersNearby = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    const configureSmartAdServer = useSmartAdServerOnOfferList();
    const refreshSmartAds = useRegionAdServerRefresh();

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            // on after list fetched
            initialFetchDone && hitSearch();
        }
    }, [list.requestState, initialFetchDone]);

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            configureSmartAdServer(singleRegion);
            refreshSmartAds();
        }
    }, [list.requestState]);

    useEffect(() => {
        isGTMInitialized && list.requestState === RequestState.Success && gtmEventOffersCount(list.count);
    }, [list.requestState, isGTMInitialized]);

    const hitSearch = useCallback(() => {
        const {count, propertiesCount, latestQuery, offers} = list;

        const selectedSearchRegions: ISearchRegion[] = selectedRegions.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData(
            count,
            propertiesCount,
            latestQuery,
            selectedSearchRegions,
            query, // needed in case of `s` query-param is present
            null, // we have no info about previous view
            travelTime,
            searchLocationTab
        );
        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        const modularHouseOffers = modularHouseCache.modularHouseOffersRest ? modularHouseCache.modularHouseOffersRest : modularHouseCache.modularHouseOffers;

        const modularHousesIds = modularHouseOffers?.results.map((offer) => offer.id) || [];

        offerListSearchHit(ViewType.OFFER_LIST, searchData, offerIds, offerNames, vendorIds, vendorNames, modularHousesIds);
    }, [list]);

    /**
     * Render
     */

    const maxPageOffer = list.count ? Math.ceil(list.count / OFFER_LIST_PAGE_SIZE) : 1;
    const maxPageSoldOffer = list.sold_offers_count ? Math.ceil(list.sold_offers_count / OFFER_LIST_PAGE_SIZE) : 1;
    const maxPage = maxPageOffer + maxPageSoldOffer - 1;

    const currentPage = typeof query.page === "string" ? parseInt(query.page, 10) : 1;

    //  Check if search slug contains special listing and return its path if available
    const getSpecialListingPath = () => {
        return specialListing.some((el) => location.pathname.includes(el)) && selectedRegions.length <= 1 ? location.pathname.replace("/s/", "") : undefined;
    };

    type SpecialListingType = "tanie-" | "gotowe-" | "luksusowe-";
    // breadcrumbs
    const specialListing: SpecialListingType[] = ["tanie-", "gotowe-", "luksusowe-"];
    const offersTypeFromSearch = useSelector((store: IRPStore) => store.offerList?.search?.type);

    const offersType = offersTypeFromSearch || list.latestQuery.type ? parseInt(list.latestQuery.type as string, 10) : null;

    // Data prepared here makes the BreadCrumbs component lighter since it is used in OfferList, OfferDetail, PropertyDetail
    const getBreadcrumbs = useMemo(() => {
        if (singleRegion && selectedRegions[0]) {
            const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
                selectedRegions[0]?.stats || {};

            return {
                region_type_voivodeship: region_type_voivodeship || null,
                region_type_city: region_type_city || null,
                region_type_county: region_type_county || null,
                region_type_district: region_type_district || null,
                region_type_housing_estate: region_type_housing_estate || null,
                region_type_town: region_type_town || null
            };
        }
        return {
            region_type_voivodeship: null,
            region_type_city: null,
            region_type_county: null,
            region_type_district: null,
            region_type_housing_estate: null,
            region_type_town: null
        };
    }, [singleRegion]);

    return (
        <>
            <article>
                <OfferListViewNavigation />
                <WideContent css={[flex("center", "space-between"), pt(1)]}>
                    <div css={[breadcrumbsWrapper, breadcrumbsWrapperWithTopReelsStyle]}>
                        <OfferAndPropertyBreadcrumbs
                            type={(offersType as OfferType) || null}
                            breadcrumbs={getBreadcrumbs}
                            specialListing={getSpecialListingPath()}
                            country={latestQuery.country}
                        />
                    </div>
                    {!!isMobile && selectedRegions.length === 1 && <OfferAveragePrice singleRegion={selectedRegions[0]} noText css={headerInfoIconWrapper} />}
                </WideContent>

                <OfferListListSection
                    ref={offerListRef}
                    list={list}
                    metaDataTitle={metaDataTitle}
                    selectedRegion={selectedRegions}
                    css={mb(3)}
                    offerListSubType={props.match.params.offerListSubType}
                />
                <SchemaListItem offers={list.offers} />
                {maxPage > 1 && (
                    <WideContent css={paginationHolder}>
                        <PaginationWithList
                            hrefBuilder={buildPageLink}
                            iconPrev={<ChevronLeftIcon size="2.4" />}
                            iconNext={<ChevronRightIcon size="2.4" />}
                            pageCount={maxPage}
                            currentPage={currentPage}
                            multiNumbersInside
                            size={isMobile ? PaginationSize.SM : PaginationSize.LG}
                        />
                    </WideContent>
                )}
                <WideContent css={mb(12)}>
                    <ListingBanner />
                </WideContent>

                {/* <WideContent>
                    <OfferWeFindForYouBox />
                </WideContent> */}
                {singleRegion && offersNearby.length ? (
                    <WideContent css={nearbyOffersHolder}>
                        <OfferListNearbyOffers
                            offerParams={offerParams}
                            viewType={ViewType.OFFER_LIST}
                            regionWhere={selectedRegions[0].name_declension_where}
                        />
                    </WideContent>
                ) : null}
                {singleRegion && articles.length > 0 && (
                    <RelatedArticlesSection
                        heading={`Wiadomości na temat ${singleRegion?.name_declension_what}:`}
                        articles={articles}
                        css={articleSectionStyles}
                    />
                )}

                {!offerListSubFilter && !latestQuery.floor_choices && list.page === 1 && search?.seo_description && (
                    <WideContent>
                        <OfferListSeoDescription seo_description={search.seo_description} />
                    </WideContent>
                )}

                {currentPage === 1 && <FaqSection faqContent={faq} />}

                {!offerListSubFilter && !latestQuery.floor_choices && singleRegion && !isListEmpty && (
                    <WideContent>
                        <OfferListGenericLocationDescription
                            offerType={offersType as OfferType}
                            filters={offerParams}
                            region={singleRegion}
                            css={locationDescriptionStyles}
                        />
                    </WideContent>
                )}

                {search && (
                    <OfferListLocationLinksWrapper css={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            nearbyRegions={nearbyRegions}
                            offerType={search.type}
                            isOfferListEmpty={!list.offers.length}
                            region={singleRegion}
                        />
                    </OfferListLocationLinksWrapper>
                )}
                {countryId !== Country.POLAND && !search && (
                    <OfferListLocationLinksWrapper css={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            offerType={Number(latestQuery.type)}
                            isOfferListEmpty={!list.offers.length}
                            country={countryId}
                            abroadVoivodeshipRegions
                        />
                    </OfferListLocationLinksWrapper>
                )}
            </article>
            <FavouritesInfoModal />
            <LeadModal />
            <ForYouModal />

            {/*
               ClickBot rendering commented due to task - CU-8695f79b0

               {currentPage === 1 && selectedRegions.length === 1 && ((offersType as OfferType) === OfferType.FLAT || (offersType as OfferType) === null) && (
                   <ClickBot
                       regionId={selectedRegions[0].id}
                       regionType={selectedRegions[0].type}
                       shouldPopupBeOpened={shouldClickBotConversationBeOpened}
                       onShouldPopupBeOpenened={() => {
                           setClickBotStorage({
                               didShowAutomatically: true
                           });
                       }}
                   />
              )}
             */}
            {(offersType as OfferType) === OfferType.FLAT || (offersType as OfferType) === null ? (
                <>
                    {currentPage > 1 && (
                        <OfferChatBot
                            shouldPopupBeOpened={shouldChatBotConversationBeOpened}
                            onShouldPopupBeOpenened={() => {
                                setChatBotStorage({
                                    didShowAutomatically: true
                                });
                            }}
                        />
                    )}
                </>
            ) : (
                <OfferChatBot
                    shouldPopupBeOpened={shouldChatBotConversationBeOpened}
                    onShouldPopupBeOpenened={() => {
                        setChatBotStorage({
                            didShowAutomatically: true
                        });
                    }}
                />
            )}
        </>
    );
};

//  Styles
const breadcrumbsWrapper = (theme: Theme) => css`
    ${flex("center", "space-between")};
    overflow-y: scroll;

    @media (min-width: ${theme.breakpoints.md}) {
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            display: none;
        }
    }
`;

const breadcrumbsWrapperWithTopReelsStyle = css`
    margin-top: -${calculateRemSize(3)};
    margin-bottom: -${calculateRemSize(2)};
`;

const headerInfoIconWrapper = css`
    ${pl(1)};
    flex: 0 0 ${calculateRemSize(4)};
    ${flex("center", "center")};
    width: ${calculateRemSize(4)};
    height: ${calculateRemSize(3)};

    & > div {
        ${pt(0)};
    }
`;

const paginationHolder = (theme: Theme) => css`
    padding: 4.7rem 0;

    @media (${theme.breakpoints.md}) {
        padding: 6rem 0;
    }
`;

const nearbyOffersHolder = (theme: Theme) => css`
    margin-bottom: 4rem;
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 8rem;
    }
`;

const locationLinksWrapper = (theme: Theme) => css`
    margin: 0 0 3.5rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 4.2rem 0;
    }
`;

const locationDescriptionStyles = (theme: Theme) => css`
    margin-bottom: 0.4rem;

    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 1.8rem;
    }
`;

const articleSectionStyles = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 3.6rem;
    }
`;
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {RouteComponentProps, useLocation, useParams} from "react-router";
import {css, Theme} from "@emotion/react";

import {WideContent} from "@pg-design/grid-module";
import {calculateRemSize, flex, mb, pl, pt} from "@pg-design/helpers-css";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {PaginationSize, PaginationWithList} from "@pg-design/pagination";
import {FaqSection} from "@pg-mono/faq";
import {useScrollObserver} from "@pg-mono/hooks";
import {parseSearch} from "@pg-mono/request";
import {RequestState} from "@pg-mono/request-state";
import {IOfferFriendlySubFilterGeneralUrlParams} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpFeatureFlagNewUserFlow} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {LocationLinks} from "../../atoms/LocationLinks";
import {useOneTap} from "../../auth/hooks/use_one_tap";
import {ForYouModal} from "../../for_you/components/ForYouModal";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {useGetOfferListModularHouses} from "../../modular_houses/hooks/use_get_modular_house_offer_list";
import {FavouritesInfoModal} from "../../my_offers/components/FavouritesInfoModal/FavouritesInfoModal";
import {ListingBanner} from "../../property_configurator/components/ListingBanner";
import {Country} from "../../region/types/Country";
import {useSearchGuideTour} from "../../search/hooks/use_search_guide_tour";
import {UseGeolocationWithAlgolytics} from "../../search/utils/use_geolocation_with_algolytics";
import {useRegionAdServerRefresh} from "../../smart_ad_server/hooks/use_region_ad_server_refresh";
import {ISearchRegion, offerListSearchHit, prepareOfferListSearchData} from "../../tracking/algolytics_hits/offer_list_search_hit";
import {gtmEventOffersCount} from "../../tracking/google_tag_manager/gtm_event_ui";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {OfferListLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {OfferChatBot} from "../components/offer_chatbot/OfferChatBot";
import {OfferListViewNavigation} from "../components/OfferListViewNavigation";
import {offerUrlParser} from "../helpers/friendly_offer_list/offer_url_parser";
import {useSmartAdServerOnOfferList} from "../hooks/use_smart_ad_server_on_offer_list";
import {ISelectedRegion} from "../list/actions/offer_list_selected_data_actions";
import {OfferAveragePrice} from "../list/components/OfferAveragePrice/OfferAveragePrice";
import {OfferListGenericLocationDescription} from "../list/components/OfferListGenericLocationDescription/OfferListGenericLocationDescription";
import {OfferListListSection} from "../list/components/OfferListListSection/OfferListListSection";
import {OfferListNearbyOffers} from "../list/components/OfferListNearbyOffers";
import {OfferListSeoDescription} from "../list/components/OfferListSeoDescription";
import {OFFER_LIST_PAGE_SIZE} from "../list/constants/offer_list";
import {offerListGuideTourName, offerListGuideTourSteps} from "../list/constants/offer_list_guide_tour";
import {getCountryId} from "../list/helpers/sub_type/get_fetch_offer_list_query";
import {useFetchOfferList} from "../list/hooks/use_fetch_offer_list";
import {usePageLinkBuilder} from "../list/hooks/usePageLinkBuilder";
import {OfferListSubType} from "../list/types/OfferListSubType";
import {SchemaListItem} from "../schemas/SchemaListItem";
import {OfferType} from "../types/OfferType";
import {getChatBotStorage, setChatBotStorage} from "../utils/chat_bot_storage";
import {getListingPageNearbyOffersPayload} from "../utils/get_listing_page_nearby_offers_payload";

interface IOwnProps {}

export interface IProps extends IOwnProps, RouteComponentProps<{offerListSubType?: OfferListSubType}> {}

export const OfferListView = (props: IProps) => {
    //  Init
    const {isMobile} = useUserDevice();
    const fetchOfferList = useFetchOfferList();
    const offerListRef = useRef<HTMLDivElement | null>(null);

    const chatBotStorage = getChatBotStorage();
    const [shouldOfferListConverstionBeOpened, setShouldOfferListConversationBeOpened] = useState(false);
    const shouldChatBotConversationBeOpened = chatBotStorage
        ? !chatBotStorage.didShowAutomatically && shouldOfferListConverstionBeOpened
        : shouldOfferListConverstionBeOpened;

    /*
       ClickBot related logic commented due to task - CU-8695f79b0

        const clickBotStorage = getClickBotStorage();
        const shouldClickBotConversationBeOpened = clickBotStorage
            ? !clickBotStorage.didShowAutomatically && shouldOfferListConverstionBeOpened
            : shouldOfferListConverstionBeOpened;
     */

    const selectedRegions = useSelector((state: IRPStore) => state.offerList.selectedRegions);
    const list = useSelector((state: IRPStore) => state.offerList.list);
    const articles = useSelector((state: IRPStore) => state.offerList.articles);
    const [initialFetchDone, setInitialFetchDone] = useState(false);

    const search = useSelector((state: IRPStore) => state.offerList.search);
    const nearbyRegions = useSelector((state: IRPStore) => state.offerList.nearbyRegions);
    const recommendedRegions = useSelector((state: IRPStore) => state.offerList.recommendedRegions);
    const metaDataTitle = useSelector((state: IRPStore) => state.metaData.standardMetaData.title);
    const regionTypes = useSelector((state: IRPStore) => (state.variables.data ? state.variables.data.region_type : []));
    const faq = useSelector((state: IRPStore) => state.faq.data);
    const travelTime = useSelector((state: IRPStore) => state.search.travelTime);
    const location = useLocation();
    const latestQuery = useSelector((state: IRPStore) => state.offerList.list.latestQuery);
    const countryId = getCountryId(latestQuery.country);
    const modularHouseCache = useGetOfferListModularHouses();
    const isListEmpty = list.offers.length === 0;
    const searchLocationTab = useSelector((state: IRPStore) => state.search.searchForm.locationSearchTab);

    const {locationAsked, locateUser} = UseGeolocationWithAlgolytics();
    const {startGuideTour, hasGuideTourBeenCompleted, guideTourCurrentStepId, tourStorageState} = useSearchGuideTour({name: offerListGuideTourName});

    useEffect(() => {
        /**
         * fetch offers on init including default distance on browser side - CU-862jpkfcy
         *
         * after dependencies changes in useEffect that triggers hitSearch search event was triggered 2 times on first list view entry, function in .then prevents this
         */
        fetchOfferList().then(() => {
            setInitialFetchDone(true);
        });
    }, []);

    useEffect(() => {
        if (!rpFeatureFlagNewUserFlow) {
            return;
        }

        // start guide tour on mount only if stage has not been set and guide tour has not been started
        if (tourStorageState && !tourStorageState?.hasBeenSeen && typeof guideTourCurrentStepId !== "number") {
            startGuideTour(offerListGuideTourSteps);
        }
    }, [tourStorageState]);

    useOneTap({
        disable: rpFeatureFlagNewUserFlow ? !hasGuideTourBeenCompleted : false
    });

    useScrollObserver(
        ({scrollY}) => {
            const offerListOffsetTop = offerListRef.current?.offsetTop || 0;
            const offerListHeight = offerListRef.current?.clientHeight || 0;
            const breakpoint = offerListHeight / 3 + offerListOffsetTop;
            const locationAskBreakpoint = (offerListHeight + offerListOffsetTop) / 10;

            if (!locationAsked && scrollY >= locationAskBreakpoint) {
                locateUser();
            }

            if (breakpoint && scrollY >= breakpoint) {
                setShouldOfferListConversationBeOpened(true);
            }
        },
        [offerListRef.current, locationAsked],
        !shouldChatBotConversationBeOpened
    );

    const singleRegion = selectedRegions.length === 1 ? selectedRegions[0] : undefined;
    const query = parseSearch(location.search);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);
    const buildPageLink = usePageLinkBuilder();
    const {offerListSubFilter} = useParams<Partial<IOfferFriendlySubFilterGeneralUrlParams>>();

    const offerParams = useMemo(() => {
        const friendlySlug = (props.match.params as {friendlySlug?: string}).friendlySlug;
        return friendlySlug ? offerUrlParser(friendlySlug) : null;
    }, [props.match.params]);
    const {data: nearbyOffersResponse} = useGetOfferListQuery(getListingPageNearbyOffersPayload({offerParams, singleRegion}), {
        skip: list.requestState !== RequestState.Success
    });
    const offersNearby = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    const configureSmartAdServer = useSmartAdServerOnOfferList();
    const refreshSmartAds = useRegionAdServerRefresh();

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            // on after list fetched
            initialFetchDone && hitSearch();
        }
    }, [list.requestState, initialFetchDone]);

    useEffect(() => {
        if (list.requestState === RequestState.Success) {
            configureSmartAdServer(singleRegion);
            refreshSmartAds();
        }
    }, [list.requestState]);

    useEffect(() => {
        isGTMInitialized && list.requestState === RequestState.Success && gtmEventOffersCount(list.count);
    }, [list.requestState, isGTMInitialized]);

    const hitSearch = useCallback(() => {
        const {count, propertiesCount, latestQuery, offers} = list;

        const selectedSearchRegions: ISearchRegion[] = selectedRegions.reduce((acc: ISearchRegion[], region: ISelectedRegion) => {
            const regionType = region && regionTypes?.find((t) => t.value === region.type);
            return [...acc, {...region, type__display: regionType ? regionType.label.toLocaleLowerCase() : ""}];
        }, []);

        const searchData = prepareOfferListSearchData(
            count,
            propertiesCount,
            latestQuery,
            selectedSearchRegions,
            query, // needed in case of `s` query-param is present
            null, // we have no info about previous view
            travelTime,
            searchLocationTab
        );
        const offerIds: number[] = [];
        const offerNames: string[] = [];
        const vendorIds: number[] = [];
        const vendorNames: string[] = [];
        offers.forEach((offer) => {
            offerIds.push(offer && offer.id);
            offerNames.push(offer && offer.name);
            vendorIds.push(offer.vendor && offer.vendor.id);
            vendorNames.push(offer.vendor && offer.vendor.name);
        });

        const modularHouseOffers = modularHouseCache.modularHouseOffersRest ? modularHouseCache.modularHouseOffersRest : modularHouseCache.modularHouseOffers;

        const modularHousesIds = modularHouseOffers?.results.map((offer) => offer.id) || [];

        offerListSearchHit(ViewType.OFFER_LIST, searchData, offerIds, offerNames, vendorIds, vendorNames, modularHousesIds);
    }, [list]);

    /**
     * Render
     */

    const maxPageOffer = list.count ? Math.ceil(list.count / OFFER_LIST_PAGE_SIZE) : 1;
    const maxPageSoldOffer = list.sold_offers_count ? Math.ceil(list.sold_offers_count / OFFER_LIST_PAGE_SIZE) : 1;
    const maxPage = maxPageOffer + maxPageSoldOffer - 1;

    const currentPage = typeof query.page === "string" ? parseInt(query.page, 10) : 1;

    //  Check if search slug contains special listing and return its path if available
    const getSpecialListingPath = () => {
        return specialListing.some((el) => location.pathname.includes(el)) && selectedRegions.length <= 1 ? location.pathname.replace("/s/", "") : undefined;
    };

    type SpecialListingType = "tanie-" | "gotowe-" | "luksusowe-";
    // breadcrumbs
    const specialListing: SpecialListingType[] = ["tanie-", "gotowe-", "luksusowe-"];
    const offersTypeFromSearch = useSelector((store: IRPStore) => store.offerList?.search?.type);

    const offersType = offersTypeFromSearch || list.latestQuery.type ? parseInt(list.latestQuery.type as string, 10) : null;

    // Data prepared here makes the BreadCrumbs component lighter since it is used in OfferList, OfferDetail, PropertyDetail
    const getBreadcrumbs = useMemo(() => {
        if (singleRegion && selectedRegions[0]) {
            const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
                selectedRegions[0]?.stats || {};

            return {
                region_type_voivodeship: region_type_voivodeship || null,
                region_type_city: region_type_city || null,
                region_type_county: region_type_county || null,
                region_type_district: region_type_district || null,
                region_type_housing_estate: region_type_housing_estate || null,
                region_type_town: region_type_town || null
            };
        }
        return {
            region_type_voivodeship: null,
            region_type_city: null,
            region_type_county: null,
            region_type_district: null,
            region_type_housing_estate: null,
            region_type_town: null
        };
    }, [singleRegion]);

    return (
        <>
            <article>
                <OfferListViewNavigation />
                <WideContent css={[flex("center", "space-between"), pt(1)]}>
                    <div css={[breadcrumbsWrapper, breadcrumbsWrapperWithTopReelsStyle]}>
                        <OfferAndPropertyBreadcrumbs
                            type={(offersType as OfferType) || null}
                            breadcrumbs={getBreadcrumbs}
                            specialListing={getSpecialListingPath()}
                            country={latestQuery.country}
                        />
                    </div>
                    {!!isMobile && selectedRegions.length === 1 && <OfferAveragePrice singleRegion={selectedRegions[0]} noText css={headerInfoIconWrapper} />}
                </WideContent>

                <OfferListListSection
                    ref={offerListRef}
                    list={list}
                    metaDataTitle={metaDataTitle}
                    selectedRegion={selectedRegions}
                    css={mb(3)}
                    offerListSubType={props.match.params.offerListSubType}
                />
                <SchemaListItem offers={list.offers} />
                {maxPage > 1 && (
                    <WideContent css={paginationHolder}>
                        <PaginationWithList
                            hrefBuilder={buildPageLink}
                            iconPrev={<ChevronLeftIcon size="2.4" />}
                            iconNext={<ChevronRightIcon size="2.4" />}
                            pageCount={maxPage}
                            currentPage={currentPage}
                            multiNumbersInside
                            size={isMobile ? PaginationSize.SM : PaginationSize.LG}
                        />
                    </WideContent>
                )}
                <WideContent css={mb(12)}>
                    <ListingBanner />
                </WideContent>

                {/* <WideContent>
                    <OfferWeFindForYouBox />
                </WideContent> */}
                {singleRegion && offersNearby.length ? (
                    <WideContent css={nearbyOffersHolder}>
                        <OfferListNearbyOffers
                            offerParams={offerParams}
                            viewType={ViewType.OFFER_LIST}
                            regionWhere={selectedRegions[0].name_declension_where}
                        />
                    </WideContent>
                ) : null}
                {singleRegion && articles.length > 0 && (
                    <RelatedArticlesSection
                        heading={`Wiadomości na temat ${singleRegion?.name_declension_what}:`}
                        articles={articles}
                        css={articleSectionStyles}
                    />
                )}

                {!offerListSubFilter && !latestQuery.floor_choices && list.page === 1 && search?.seo_description && (
                    <WideContent>
                        <OfferListSeoDescription seo_description={search.seo_description} />
                    </WideContent>
                )}

                {currentPage === 1 && <FaqSection faqContent={faq} />}

                {!offerListSubFilter && !latestQuery.floor_choices && singleRegion && !isListEmpty && (
                    <WideContent>
                        <OfferListGenericLocationDescription
                            offerType={offersType as OfferType}
                            filters={offerParams}
                            region={singleRegion}
                            css={locationDescriptionStyles}
                        />
                    </WideContent>
                )}

                {search && (
                    <OfferListLocationLinksWrapper css={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            nearbyRegions={nearbyRegions}
                            offerType={search.type}
                            isOfferListEmpty={!list.offers.length}
                            region={singleRegion}
                        />
                    </OfferListLocationLinksWrapper>
                )}
                {countryId !== Country.POLAND && !search && (
                    <OfferListLocationLinksWrapper css={locationLinksWrapper}>
                        <LocationLinks
                            recommendedRegions={recommendedRegions}
                            offerType={Number(latestQuery.type)}
                            isOfferListEmpty={!list.offers.length}
                            country={countryId}
                            abroadVoivodeshipRegions
                        />
                    </OfferListLocationLinksWrapper>
                )}
            </article>
            <FavouritesInfoModal />
            <LeadModal />
            <ForYouModal />

            {/*
               ClickBot rendering commented due to task - CU-8695f79b0

               {currentPage === 1 && selectedRegions.length === 1 && ((offersType as OfferType) === OfferType.FLAT || (offersType as OfferType) === null) && (
                   <ClickBot
                       regionId={selectedRegions[0].id}
                       regionType={selectedRegions[0].type}
                       shouldPopupBeOpened={shouldClickBotConversationBeOpened}
                       onShouldPopupBeOpenened={() => {
                           setClickBotStorage({
                               didShowAutomatically: true
                           });
                       }}
                   />
              )}
             */}
            {(offersType as OfferType) === OfferType.FLAT || (offersType as OfferType) === null ? (
                <>
                    {currentPage > 1 && (
                        <OfferChatBot
                            shouldPopupBeOpened={shouldChatBotConversationBeOpened}
                            onShouldPopupBeOpenened={() => {
                                setChatBotStorage({
                                    didShowAutomatically: true
                                });
                            }}
                        />
                    )}
                </>
            ) : (
                <OfferChatBot
                    shouldPopupBeOpened={shouldChatBotConversationBeOpened}
                    onShouldPopupBeOpenened={() => {
                        setChatBotStorage({
                            didShowAutomatically: true
                        });
                    }}
                />
            )}
        </>
    );
};

//  Styles
const breadcrumbsWrapper = (theme: Theme) => css`
    ${flex("center", "space-between")};
    overflow-y: scroll;

    @media (min-width: ${theme.breakpoints.md}) {
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            display: none;
        }
    }
`;

const breadcrumbsWrapperWithTopReelsStyle = css`
    margin-top: -${calculateRemSize(3)};
    margin-bottom: -${calculateRemSize(2)};
`;

const headerInfoIconWrapper = css`
    ${pl(1)};
    flex: 0 0 ${calculateRemSize(4)};
    ${flex("center", "center")};
    width: ${calculateRemSize(4)};
    height: ${calculateRemSize(3)};

    & > div {
        ${pt(0)};
    }
`;

const paginationHolder = (theme: Theme) => css`
    padding: 4.7rem 0;

    @media (${theme.breakpoints.md}) {
        padding: 6rem 0;
    }
`;

const nearbyOffersHolder = (theme: Theme) => css`
    margin-bottom: 4rem;
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 8rem;
    }
`;

const locationLinksWrapper = (theme: Theme) => css`
    margin: 0 0 3.5rem 0;

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 4.2rem 0;
    }
`;

const locationDescriptionStyles = (theme: Theme) => css`
    margin-bottom: 0.4rem;

    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 1.8rem;
    }
`;

const articleSectionStyles = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 3.6rem;
    }
`;
